import React from 'react';
import { Breadcrumbs as MUIBreadcrumbs, Typography, Link, Box } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';

// Map of paths to their display names
const breadcrumbNameMap = {
    '/host-applications': 'Become Host Applications',
    '/host-applications/view-details': 'View Details',
    '/join-with-us': 'Join With Us',
    '/dashboard': 'Dashboard',
    '/user': 'Users',
    '/admin-users': 'Admin Users',
    '/profile': 'Profile',
    '/retreat': 'Retreats',
    '/user-retreats': 'User Retreats',
    '/retreat/view-details': 'View Details',
    '/retreat/bookings': 'Retreat Bookings',
    '/category-management': 'Category Management',
    '/add-category': 'Add Category',
    '/bookings': 'Bookings',
    '/user-bookings': 'User Bookings',
    '/bookings/view-details': 'Booking Details',
    '/payment-transactions': 'Payment Transactions',
    '/payment-methods': 'Payment Methods',
    '/payment-methods/view-details': 'Account Details',
    '/payment-accounts': 'Payment Accounts',
    '/payment-accounts/view-details': 'Account Details',
    '/hubs-locations': 'Hubs Locations',
    '/hubs-locations/add': 'Add Hub Location',
    '/hubs-locations/edit': 'Edit Hub Location',
    '/content-hub': 'Content Hub',
    '/content-hub/featured-logos': 'Featured Logos',
    '/content-hub/testimonials': 'Testimonials',
    '/content-hub/contact-us': 'Contact Us',
    '/content-hub/enquiries': 'Enquiries',
    '/content-hub/jobs': 'Careers Management',
    '/content-hub/cms-tools': 'CMS Tools',
    '/content-hub/cancellation-policy-types': 'Cancellation Policy Types',
    '/content-hub/faqs-types': 'Faqs Types',
    '/content-hub/faqs': 'Faqs',
    '/content-hub/country-codes': 'Country Codes',
    '/content-hub/founders': 'Founders',
    '/content-hub/social-media': 'Social Media',
    '/members': 'Members',
    '/members/facilitators': 'Facilitators',
    '/members/centers': 'Centers',
    '/members/centers/categories': 'Center Categories',
    '/members/organizers': 'Organizers',
    '/languages': 'Languages',
    '/content-hub/jobs/job-categories': 'Job Categories',
    '/content-hub/jobs/job-roles': 'Job Roles',
    '/content-hub/jobs/job-enquiries': 'Job Enquiries',
    '/press-management': 'Press Management',
    '/press-management/add': 'Add Press Data',
    '/press-management/edit': 'Edit Press Data',
    '/press-management/news': 'Press News',
    '/press-management/news/add': 'Add Press News',
    '/press-management/news/edit': 'Edit Press News',
    '/blogs-management': 'Blogs Management',
    '/blogs-management/add': 'Add Blog',
    '/blogs-management/edit': 'Edit Blog',
    '/blogs-management/categories': 'Blog Categories',
    '/blogs-management/categories/add': 'Add Blog Category',
    '/blogs-management/categories/edit': 'Edit Blog Category',
    '/hosts': 'Hosts',
    '/hosts/kyc': 'KYC Verification',
    '/hosts/profile': 'Host Profile',
    '/hosts-and-partners': 'Hosts & Partners',
    '/affiliates': 'Affiliates',
    '/affiliates/links': 'Links & Clicks',
    '/affiliates/ledger': 'Commission Ledger',
    '/affiliate-commission': 'Affiliate Commission',
    '/taxes-and-fees': 'Taxes and Fees',
    '/roles-permissions': 'Roles & Permissions',
    '/roles-permissions/add': 'Add Role',
    '/roles-permissions/edit': 'Edit Role',
    '/add-retreat': 'Add Retreat',
    '/add-retreat/atmosphere': 'Atmosphere',
    '/add-retreat/features': 'Features',
    '/add-retreat/packages': 'Packages',
    '/add-retreat/not-include-packages': 'Not Included',
    '/add-retreat/facilities': 'Facilities',
    '/add-retreat/surrounding-areas': 'Surrounding Areas',
    '/add-retreat/facilities-options': 'Facilities Options',
    '/add-retreat/things-in-area': 'Things in Area',
    '/add-retreat/airports': 'Airports',
    '/add-retreat/visibility-plans': 'Visibility Plans',
    '/add-retreat/visibility-factors': 'Visibility Factors',
    '/add-retreat/addons': 'Addons',
    '/add-retreat/meals': 'Meals',
    '/add-retreat/drinks': 'Drinks',
    '/add-retreat/diet-options': 'Diet Options',
    '/review-management': 'Review Management',
    '/review-management/questions': 'Review Questions',
    '/review-management/reviews': 'Reviews',
};

// Paths that are just containers and should NOT be clickable
const nonLinkablePaths = [
    '/members',
    '/content-hub',
    '/add-retreat',
    '/review-management',
    '/roles-permissions/edit',
    '/hubs-locations/edit',
    '/press-management/edit',
    '/press-management/news/edit',
    '/blogs-management/edit',
    '/blogs-management/categories/edit',
    '/become-hosts',
    '/become-hosts/view-details'
];

// Path segments that should be completely hidden from breadcrumbs
const hiddenPathSegments = ['sub-categories'];

const Breadcrumbs = () => {
    const location = useLocation();
    const pathnames = location.pathname.split('/').filter((x) => x);

    const getDisplayName = (path, value) => {
        if (breadcrumbNameMap[path]) {
            return breadcrumbNameMap[path];
        }

        const pathParts = path.split('/');
        const secondLastPart = pathParts[pathParts.length - 2];

        if (secondLastPart === 'sub-categories') return 'Sub Category List';
        if (secondLastPart === 'edit' && pathParts.includes('roles-permissions')) return 'Edit Role';
        if (secondLastPart === 'bookings' && pathParts.includes('retreat')) return 'Booking List';
        if (secondLastPart === 'view-details') return 'View Details';

        if (!isNaN(value) || value.length > 20) {
            return 'Details';
        }

        return value.charAt(0).toUpperCase() + value.slice(1).replace(/-/g, ' ');
    };

    return (
        <Box sx={{ mt: 1 }}>
            <MUIBreadcrumbs
                aria-label="breadcrumb"
                separator={<ChevronRight size={16} color="#BDBDBD" strokeWidth={2.5} />}
                sx={{
                    '& .MuiBreadcrumbs-ol': {
                        alignItems: 'center',
                    },
                }}
            >
                <Link
                    component={RouterLink}
                    to="/dashboard"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: '#666',
                        textDecoration: 'none',
                        transition: 'all 0.2s',
                    }}
                >
                    <Home size={18} />
                </Link>

                {pathnames.map((value, index) => {
                    if (hiddenPathSegments.includes(value)) return null;

                    // Skip displaying raw numeric ID if preceded by view-details or details segment
                    if (index > 0 && !isNaN(value) && (pathnames[index - 1] === 'view-details' || pathnames[index - 1] === 'edit')) {
                        return null;
                    }

                    const isNextItemNumericId = index < pathnames.length - 1 && !isNaN(pathnames[index + 1]);
                    const last = index === pathnames.length - 1 || isNextItemNumericId;
                    const to = `/${pathnames.slice(0, index + 1).join('/')}`;
                    const displayName = getDisplayName(to, value);
                    const isLinkable = !nonLinkablePaths.includes(to);

                    if (index === 0 && value === 'dashboard') return null;

                    if (last || !isLinkable) {
                        return (
                            <Typography key={to} sx={{ color: last ? '#222' : '#666', fontSize: '0.9rem', letterSpacing: '0.3px', }}>
                                {displayName}
                            </Typography>
                        );
                    }

                    return (
                        <Link
                            component={RouterLink}
                            to={to}
                            key={to}
                            sx={{
                                color: '#666',
                                textDecoration: 'none',
                                fontSize: '0.9rem',
                                fontWeight: 500,
                            }}
                        >
                            {displayName}
                        </Link>
                    );
                })}
            </MUIBreadcrumbs>
        </Box>
    );
};

export default Breadcrumbs;
